/** The intake panel (UX-SPEC §4.2): Document, Paste and URL tabs, one primary button, recent analyses. */
import { useRef, useState } from 'preact/hooks';
import type { RecentItem } from '../../shared/protocol.ts';
import {
	chooseFile,
	hasInput,
	loadSample,
	openRecent,
	pasteChanged,
	removeRecent,
	run,
} from '../controller.ts';
import { durationText } from '../estimate.ts';
import { fmtBytes, fmtDate, fmtInt } from '../format.ts';
import { analysisPriorMs } from '../rates.ts';
import { SAMPLE_TEXT } from '../sample.ts';
import { file, pasteText, pasteWords, phase, recent, tab, urlText } from '../state.ts';
import { INTAKE_EXPLAIN as IX } from '../strings-explain.ts';
import { INTAKE } from '../strings.ts';
import { Dialog, Tabs } from '../ui.tsx';

const ACCEPT = '.txt,.md,.markdown,.html,.htm,.xhtml,.docx,.pdf';

function DocumentTab() {
	const input = useRef<HTMLInputElement>(null);
	const f = file.value;
	const busy = phase.value === 'reading';
	return (
		<div class="dropzone" id="panel-document" role="tabpanel" aria-labelledby="tab-document">
			<p class="dropzone-text">{INTAKE.dropZone}</p>
			<input
				ref={input}
				type="file"
				class="visually-hidden"
				accept={ACCEPT}
				tabIndex={-1}
				aria-hidden="true"
				data-testid="file-input"
				onChange={(e) => {
					const chosen = (e.currentTarget as HTMLInputElement).files?.[0];
					if (chosen) void chooseFile(chosen);
					(e.currentTarget as HTMLInputElement).value = '';
				}}
			/>
			<button
				type="button"
				class="btn btn-secondary"
				disabled={busy}
				onClick={() => input.current?.click()}
			>
				{f ? INTAKE.changeFile : INTAKE.chooseFile}
			</button>
			<p class="dim">{INTAKE.accepted}</p>
			<p class="dim" data-testid="intake-formats">
				{IX.formats}
			</p>
			<button type="button" class="btn-text" onClick={() => loadSample(SAMPLE_TEXT)}>
				{INTAKE.loadSample}
			</button>
			{f ? (
				<p class="chosen" data-testid="chosen-file">
					{INTAKE.chosen(f.name, fmtBytes(f.size), INTAKE.formatNames[f.format])}
				</p>
			) : null}
		</div>
	);
}

function PasteTab() {
	const words = pasteWords.value;
	return (
		<div id="panel-paste" role="tabpanel" aria-labelledby="tab-paste">
			<label class="field-label" for="paste-box">
				{INTAKE.pasteLabel}
			</label>
			<textarea
				id="paste-box"
				class="textarea"
				rows={10}
				value={pasteText.value}
				onInput={(e) => pasteChanged((e.currentTarget as HTMLTextAreaElement).value)}
			/>
			<div class="paste-foot">
				<span aria-live="polite" data-testid="paste-words">
					{words === null ? '' : INTAKE.pasteWords(fmtInt(words))}
				</span>
				<button type="button" class="btn-text" onClick={() => loadSample(SAMPLE_TEXT)}>
					{INTAKE.loadSample}
				</button>
			</div>
			<p class="dim">{INTAKE.pasteHint}</p>
		</div>
	);
}

function UrlTab() {
	return (
		<div id="panel-url" role="tabpanel" aria-labelledby="tab-url">
			<label class="field-label" for="url-box">
				{INTAKE.urlLabel}
			</label>
			<input
				id="url-box"
				class="input"
				type="url"
				inputMode="url"
				autocomplete="off"
				placeholder={INTAKE.urlPlaceholder}
				value={urlText.value}
				onInput={(e) => {
					urlText.value = (e.currentTarget as HTMLInputElement).value;
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && hasInput()) void run();
				}}
			/>
			<button type="button" class="btn-text" onClick={() => loadSample(SAMPLE_TEXT)}>
				{INTAKE.loadSample}
			</button>
		</div>
	);
}

function Recent() {
	const [removing, setRemoving] = useState<RecentItem | null>(null);
	const items = recent.value;
	return (
		<section class="recent" aria-labelledby="recent-h">
			<h2 id="recent-h">{INTAKE.recent}</h2>
			{items.length === 0 ? (
				<p class="dim">{INTAKE.recentEmpty}</p>
			) : (
				<ul class="recent-list">
					{items.map((it) => (
						<li key={it.key}>
							<div>
								<strong>{it.name}</strong>
								<div class="dim">
									{INTAKE.recentLine(
										fmtInt(it.words),
										it.index === null ? '—' : it.index.toFixed(1),
										fmtDate(it.at)
									)}
									{it.stale ? ` · ${INTAKE.earlierEngine}` : ''}
								</div>
							</div>
							<div class="row-actions">
								<button type="button" class="btn btn-secondary" onClick={() => openRecent(it)}>
									{it.stale ? INTAKE.runAgainToOpen : INTAKE.open}
									<span class="visually-hidden"> {it.name}</span>
								</button>
								<button type="button" class="btn-text" onClick={() => setRemoving(it)}>
									{INTAKE.remove}
									<span class="visually-hidden"> {it.name}</span>
								</button>
							</div>
						</li>
					))}
				</ul>
			)}
			<Dialog
				title={INTAKE.removeTitle}
				open={removing !== null}
				onClose={() => setRemoving(null)}
				actions={
					<>
						<button type="button" class="btn btn-secondary" onClick={() => setRemoving(null)}>
							{INTAKE.cancel}
						</button>
						<button
							type="button"
							class="btn btn-primary"
							onClick={() => {
								if (removing) removeRecent(removing);
								setRemoving(null);
							}}
						>
							{INTAKE.removeConfirm}
						</button>
					</>
				}
			>
				<p>{removing ? INTAKE.removeBody(removing.name) : ''}</p>
			</Dialog>
		</section>
	);
}

/** P12: the expected time, from the word count when the intake has one (a pasted text); otherwise the general line. */
function IntakeEstimate() {
	const t = tab.value;
	const words = pasteWords.value;
	let text: string | null = null;
	if (t === 'paste' && words !== null && words > 0) {
		text = IX.estimate(durationText(analysisPriorMs(words)), fmtInt(words));
	} else if (t === 'document' && file.value) {
		text = IX.estimateUnknown;
	}
	if (text === null) return null;
	return (
		<p class="dim intake-estimate" data-testid="intake-estimate" aria-live="polite">
			{text}
		</p>
	);
}

export function Intake({ disabled }: { disabled?: boolean }) {
	const t = tab.value;
	const [over, setOver] = useState(false);
	return (
		<div
			class={`intake${over ? ' is-over' : ''}`}
			onDragOver={(e) => {
				e.preventDefault();
				setOver(true);
			}}
			onDragLeave={() => setOver(false)}
			onDrop={(e) => {
				e.preventDefault();
				setOver(false);
				const dropped = e.dataTransfer?.files?.[0];
				if (dropped) void chooseFile(dropped);
			}}
		>
			<h1>{INTAKE.title}</h1>
			<p class="dim">{INTAKE.help}</p>
			<p class="dim" data-testid="intake-you-get">
				{IX.youGet}
			</p>
			<p class="dim" data-testid="intake-privacy">
				{IX.privacy}
			</p>
			<Tabs
				label={INTAKE.tablist}
				tabs={[
					{ id: 'document', label: INTAKE.tabs.document },
					{ id: 'paste', label: INTAKE.tabs.paste },
					{ id: 'url', label: INTAKE.tabs.url },
				]}
				value={t}
				onChange={(v) => {
					tab.value = v;
				}}
			/>
			{t === 'document' ? <DocumentTab /> : t === 'paste' ? <PasteTab /> : <UrlTab />}
			<div class="intake-run">
				<button
					type="button"
					class="btn btn-primary btn-lg"
					disabled={disabled || !hasInput()}
					onClick={() => void run()}
				>
					{INTAKE.run}
				</button>
				<IntakeEstimate />
			</div>
			<Recent />
		</div>
	);
}
